/**
 * Qué cara le ponemos a una carpeta en el grid.
 *
 * ## Por qué esto existe: el diseño pide una imagen que el contrato no tiene
 *
 * La pantalla G dibuja las carpetas en un grid 2×2 **con imagen de cabecera**, y
 * `V2ClientDocumentFolderSchema` publica exactamente cuatro campos: `id`, `name`, `parent` y
 * `order`. No hay imagen, y no la va a haber sin un campo nuevo, una UI de back-office para
 * cargarla y alguien que la cargue en cada carpeta de cada proyecto.
 *
 * La referencia de UI además aclara que **los `image-slot` del bundle son marcadores, no assets**
 * (`docs/design/referencia-ui-portal-cliente.md` §6). Así que la cabecera se resuelve con ícono y
 * color derivados del nombre: cero backend, cero migración, y el día que exista una imagen real
 * este módulo se reemplaza sin tocar las pantallas.
 *
 * ## «Pero `document-kind.ts` dice explícitamente que NO se adivina por el nombre»
 *
 * Dice eso, y las dos cosas son correctas — porque lo que se arriesga es distinto.
 *
 * Adivinar el tipo de archivo por el nombre abre el **visor equivocado**: un «Contrato final.pdf»
 * que en realidad es un .docx muestra una pantalla rota. Es un fallo funcional.
 *
 * Adivinar el ícono es cosmético y **degrada a neutro**: si el nombre no matchea nada, la carpeta
 * sale con el ícono genérico de carpeta, que es exactamente lo que se vería si no existiera este
 * archivo. Nunca hay una versión «rota» del resultado, sólo una menos linda.
 *
 * Por eso acá el fallback no es un caso de error, es el caso NORMAL: los nombres los escribe una
 * persona en el back-office y la mayoría no va a matchear.
 */

export const FolderLook = {
  plans: 'plans',
  legal: 'legal',
  payments: 'payments',
  photos: 'photos',
  regulations: 'regulations',
  deliveries: 'deliveries',
  /** El caso normal, no el excepcional: nombre que no matchea ninguna familia conocida. */
  generic: 'generic'
} as const

export type FolderLook = (typeof FolderLook)[keyof typeof FolderLook]

/**
 * Nombre → forma comparable.
 *
 * Los acentos se sacan a propósito: el nombre lo escribe una persona y «Eléctricos», «Electricos»
 * y «ELÉCTRICOS» son la misma carpeta para cualquiera que no sea un `===`. La descomposición NFD
 * separa la letra de su tilde, y el rango `U+0300–U+036F` —los diacríticos combinantes— borra las
 * tildes que quedaron sueltas. Escrito con escapes y no con los caracteres literales: son
 * invisibles en el editor y nadie puede revisar lo que no ve.
 */
const DIACRITICS = /[\u0300-\u036f]/g

function normalize(name: string): string {
  return name.normalize('NFD').replace(DIACRITICS, '').toLowerCase().trim()
}

/**
 * Las reglas, **en orden**: gana la primera que matchea.
 *
 * El orden no es estético, resuelve solapamientos reales. «Escrituras y planos» tiene que caer en
 * legal y no en planos, porque lo que manda es de qué trata la carpeta, no qué palabra aparece
 * primero. Cuando dudes, poné la familia más específica arriba.
 *
 * Las claves están normalizadas de antemano (sin tildes, en minúscula): se comparan contra la
 * salida de `normalize`, no contra el nombre crudo.
 */
const RULES: readonly { look: FolderLook; keywords: readonly string[] }[] = [
  {
    look: FolderLook.legal,
    keywords: ['legal', 'juridic', 'contrato', 'escritura', 'notarial', 'promesa', 'poder']
  },
  {
    look: FolderLook.payments,
    keywords: ['pago', 'cuota', 'recibo', 'factura', 'estado de cuenta', 'financ', 'abono']
  },
  { look: FolderLook.deliveries, keywords: ['entrega', 'inspeccion', 'garantia'] },
  {
    look: FolderLook.regulations,
    keywords: ['reglamento', 'normativ', 'condominio', 'copropiedad', 'convivencia']
  },
  {
    look: FolderLook.plans,
    keywords: [
      'plano',
      'arquitect',
      'diseno',
      'render',
      'electric',
      'hidraul',
      'sanitar',
      'estructur'
    ]
  },
  { look: FolderLook.photos, keywords: ['foto', 'imagen', 'galeria', 'avance', 'obra'] }
]

export function folderLook(name: string): FolderLook {
  const normalized = normalize(name)

  for (const rule of RULES) {
    if (rule.keywords.some((keyword) => normalized.includes(keyword))) return rule.look
  }

  return FolderLook.generic
}
